<template>
    <FreelancerSignupStep data-testid="freelancer-signup-step-your-freelancing-experience" :save-step="handleSaveStep" :is-cta-next-visible="false">
        <template #freelancer-signup-step-title>{{ t('profile.signup.step.freelancing-experience.title') }}</template>
        <template #freelancer-signup-step-main>
            <FreelancingExperienceOptions
                data-testid="freelancer-signup-step-your-freelancing-experience-cards"
                :freelancing-experience-options="freelancingExperienceLevels"
                :selected-freelancing-experience-id="freelancingExperience"
                @freelancing-experience:selected="handleLevelOfExperienceAsFreelancerSelected"
            />
        </template>
    </FreelancerSignupStep>
</template>
<script setup lang="ts">
    import {computed, ref} from 'vue';
    import {definePageMeta} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {createError, useRoute, useTranslation} from '#imports';
    import {freelancerSignupEventBus} from '~/config/freelancer-signup-events';
    import {FreelancerSignupContext} from '~/features/freelancer-signup/_common/types/FreelancerSignupContext';
    import {useGetInvitationCode} from '~/features/freelancer-signup/invitation-code/composables/useGetInvitationCode';
    import {FreelancerSignupStepName} from '~/features/freelancer-signup/navigation/types/FreelancerSignupStep';
    import FreelancerSignupStep from '~/features/freelancer-signup/steps/ui/FreelancerSignupStep.vue';
    import {useFreelancerSignupStore} from '~/features/freelancer-signup/workflow/store/useFreelancerSignupStore';
    import {type FreelancingExperience, FreelancingExperienceId} from '~/features/freelancing-experience/types/FreelancingExperience';
    import FreelancingExperienceOptions from '~/features/freelancing-experience/ui/FreelancingExperienceOptions.vue';
    import {CorporateProgram} from '~/openapi';

    definePageMeta({
        funnelStepName: FreelancerSignupStepName.FREELANCING_EXPERIENCE,
        funnelWithSingleColumn: true,
        middleware: [
            function (to, from) {
                if (import.meta.client) return;
                if (to.query.corporateProgram) {
                    const normalizedValue = String(to.query.corporateProgram).toUpperCase();

                    if (!Object.values(CorporateProgram).includes(normalizedValue as CorporateProgram)) {
                        throw createError({statusCode: 400, statusMessage: 'Invalid corporate program parameter'});
                    }
                }
            },
        ],
    });

    // Stores
    const freelancerSignupStore = useFreelancerSignupStore();
    const {workflow} = storeToRefs(freelancerSignupStore);

    // Composable
    const {t} = useTranslation();
    const route = useRoute();

    // Refs
    const freelancingExperience = ref<FreelancingExperienceId>();
    const corporateProgram = computed(() =>
        route.query.corporateProgram ? (String(route.query.corporateProgram).toUpperCase() as CorporateProgram) : undefined,
    );

    const isStrategyContext = computed(() => workflow.value?.context === FreelancerSignupContext.STRATEGY);
    const isOpenContext = computed(() => workflow.value?.context === FreelancerSignupContext.OPEN);

    const freelancingExperienceLevels = computed<FreelancingExperience[]>(() => [
        {
            id: FreelancingExperienceId.STARTING,
            title: t('profile.signup.step.freelancing-experience.options.starting'),
            name: 'one-block--high',
        },
        {
            id: FreelancingExperienceId.INTERMEDIATE,
            title: t('profile.signup.step.freelancing-experience.options.intermediate'),
            name: 'few-blocks--high',
        },
        {
            id: FreelancingExperienceId.EXPERT,
            title: t('profile.signup.step.freelancing-experience.options.expert'),
            name: 'many-blocks--high',
        },
    ]);

    // Handlers
    function handleLevelOfExperienceAsFreelancerSelected(levelId: FreelancingExperienceId) {
        freelancingExperience.value = levelId;
        freelancerSignupEventBus.emit('freelancer-signup-step:go-to-first-step-to-complete', {save: true});
    }

    async function handleSaveStep(): Promise<boolean> {
        if (!freelancingExperience.value) {
            return false;
        }

        if (!isStrategyContext.value && !isOpenContext.value) {
            const {getInvitationCode} = useGetInvitationCode();

            await freelancerSignupStore.initWorkflowIfNeeded({
                context: FreelancerSignupContext.REGULAR,
                invitationCode: getInvitationCode(),
                corporateProgram: corporateProgram.value,
                freelancingExperience: freelancingExperience.value,
            });
        } else {
            await freelancerSignupStore.saveFreelancingExperience({freelancingExperience: freelancingExperience.value});
        }

        return true;
    }
</script>
